/**
 * ProductCard Component
 *
 * Self-contained product card with image area, description, price,
 * flavour selector, quantity selector, add-to-cart button and toast.
 *
 * Used by:
 * - ProductSection (homepage 3-up grid)
 * - BundleLanding (per-bundle landing pages for Instagram shop tagging)
 */

import React, { useState, useRef } from 'react';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/formatPrice';
import { FlavorSelector } from './FlavorSelector';
import { QuantitySelector } from './QuantitySelector';
import { MultiFlavorSelector } from './MultiFlavorSelector';

// Single-tub images (new tub artwork)
export const BOTTLE_IMAGES = {
  LIME: '/images/lime-tub.webp',
  ORANGE: '/images/orange-tub.webp',
  PINEAPPLE: '/images/pineapple-tub.webp',
};

// Per-pack tropical background art (heading is baked into the image)
export const PACK_BACKGROUNDS: Record<string, string> = {
  'one-and-done': '/images/pack-bg-01.webp',
  'dynamic-duo': '/images/pack-bg-02.webp',
  'triple-threat': '/images/pack-bg-03.webp',
};

export const getFlavorImage = (flavor: string) => {
  switch (flavor) {
    case 'Lime Squash':
      return BOTTLE_IMAGES.LIME;
    case 'Orange Crush':
      return BOTTLE_IMAGES.ORANGE;
    case 'Pineapple Punch':
      return BOTTLE_IMAGES.PINEAPPLE;
    default:
      return BOTTLE_IMAGES.LIME;
  }
};

// Toast notification component
interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
  onViewCart?: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, onViewCart }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-6 right-6 z-50 transition-all duration-300 transform translate-y-0 opacity-100 animate-slide-down">
      <div
        className={`px-6 py-4 rounded-lg shadow-lg ${
          type === 'success'
            ? 'bg-gradient-to-r from-purple-900/90 to-fuchsia-900/90 border border-fuchsia-500/30'
            : 'bg-red-900/90 border border-red-500/30'
        } backdrop-blur-md`}
      >
        <div className="flex items-center">
          <div className={`mr-3 text-2xl ${type === 'success' ? 'text-fuchsia-400' : 'text-red-400'}`}>
            {type === 'success' ? '✓' : '✗'}
          </div>
          <p className="text-white font-medium">{message}</p>
          <button
            onClick={onClose}
            className="ml-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close notification"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {type === 'success' && onViewCart && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => {
                onViewCart();
                onClose();
              }}
              className="px-4 py-1.5 bg-gradient-to-r from-[#ff00ff] via-[#ff66ff] to-[#00ffff] text-white text-sm rounded-full hover:opacity-90 transition-all flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
              View Cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Product card component with 2-column internal layout
export interface ProductCardProps {
  id: string;
  name: string;
  description: string;
  price: number;
  imagePlaceholder: string;
  gradientFrom: string;
  gradientTo: string;
  buttonFrom: string;
  buttonTo: string;
  tier?: string;
  freeShipping?: boolean;
  shippingCost?: number;
  productFlavor?: string;
  availableFlavors?: string[];
  /**
   * When true, image area uses a shorter 5:4 ratio on mobile so the
   * add-to-cart button stays closer to the fold on single-bundle
   * landing pages. Defaults to false (square on all sizes) to keep
   * the homepage layout unchanged.
   */
  compactMobile?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  description,
  price,
  imagePlaceholder,
  gradientFrom,
  gradientTo,
  tier = 'Basic',
  freeShipping = false,
  shippingCost = 995,
  productFlavor = 'Lime Squash',
  availableFlavors = ['Lime Squash', 'Orange Crush', 'Pineapple Punch'],
  compactMobile = false,
}) => {
  const packBackground = PACK_BACKGROUNDS[id];
  const [flavor, setFlavor] = useState(productFlavor);
  const [selectedFlavors, setSelectedFlavors] = useState<string[]>(() => {
    if (id === 'dynamic-duo') {
      return [productFlavor !== 'Lime Squash' ? productFlavor : 'Lime Squash', 'Orange Crush'];
    } else if (id === 'triple-threat') {
      return ['Lime Squash', 'Orange Crush', 'Pineapple Punch'];
    }
    return [];
  });
  const [quantity, setQuantity] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const { addToCart, setIsCartOpen } = useCart();
  const [currentBottleImage, setCurrentBottleImage] = useState<string>(() => getFlavorImage(productFlavor));
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [nextImage, setNextImage] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    if (id === 'dynamic-duo' || id === 'triple-threat') return;
    const newImage = getFlavorImage(flavor);

    if (newImage === currentBottleImage) {
      return;
    }

    setNextImage(newImage);
    setIsImageLoading(true);

    const img = new Image();
    img.src = newImage;

    img.onload = () => {
      setCurrentBottleImage(newImage);
      setIsImageLoading(false);
    };

    img.onerror = () => {
      setCurrentBottleImage(imagePlaceholder);
      setIsImageLoading(false);
    };
  }, [flavor, id]);

  const handleAddToCart = () => {
    if (id === 'dynamic-duo' || id === 'triple-threat') {
      addToCart({
        id,
        name,
        description: `Premium ${name}`,
        images: [getFlavorImage(selectedFlavors[0] || 'Lime Squash')],
        unitPrice: price,
        quantity,
        flavor: selectedFlavors.join(', '),
        freeShipping: hasFreeShipping,
      });
    } else {
      addToCart({
        id,
        name,
        description: `Premium ${name}`,
        images: [currentBottleImage],
        unitPrice: price,
        quantity,
        flavor,
        freeShipping: hasFreeShipping,
      });
    }

    setShowToast(true);
    setIsCartOpen(true);
  };

  const totalPrice = price * quantity;
  const hasFreeShipping = true;

  return (
    <div className="glass-panel rounded-3xl p-6 h-full relative isolate">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
        <div className="flex flex-col h-full">
          <div
            className={`${compactMobile ? 'aspect-[5/4] md:aspect-square' : 'aspect-square'} ${packBackground ? '' : `bg-gradient-to-br from-${gradientFrom}-500/20 to-${gradientTo}-500/20`} rounded-2xl overflow-hidden mb-4 relative group flex items-center justify-center`}
            style={packBackground ? { backgroundImage: `url(${packBackground})`, backgroundSize: 'cover', backgroundPosition: 'top center' } : undefined}
          >
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/10 z-10">
                <div className="w-8 h-8 border-t-2 border-b-2 border-[#ff00ff] rounded-full animate-spin"></div>
              </div>
            )}
            {id === 'dynamic-duo' || id === 'triple-threat' ? (
              <div className="w-full h-full flex items-center justify-center gap-2 px-2">
                {selectedFlavors.map((f, i) => (
                  <img
                    key={`${i}-${f}`}
                    src={getFlavorImage(f)}
                    alt={`${name} tub ${i + 1}: ${f}`}
                    className={`max-h-[45%] ${id === 'triple-threat' ? 'max-w-[30%]' : 'max-w-[42%]'} object-contain transition-all duration-300`}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = imagePlaceholder;
                    }}
                  />
                ))}
              </div>
            ) : (
              <img
                ref={imageRef}
                src={currentBottleImage}
                alt={name}
                className={`max-h-[65%] mx-auto object-contain transition-all duration-300 ${isImageLoading ? 'opacity-80 scale-95' : 'opacity-100'}`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = imagePlaceholder;
                }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <h3 className="sr-only">{name}</h3>
          <p className="text-white font-bold mb-4 flex-grow">{description}</p>

          <div className="mt-auto">
            <p className="text-xl font-bold">${formatPrice(totalPrice)}</p>
            <div className="flex items-center gap-2 mt-1">
              {hasFreeShipping ? (
                <p className="text-fuchsia-400 text-sm flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <path d="M3 3v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V3"></path>
                    <path d="M2 12h20v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-7z"></path>
                    <path d="M12 12v9"></path>
                    <path d="M12 3v9"></path>
                  </svg>
                  Free Shipping Aus Wide
                </p>
              ) : (
                <p className="text-gray-400 text-sm flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <rect width="18" height="12" x="3" y="8" rx="2"></rect>
                    <path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  + Shipping (${formatPrice(shippingCost)})
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col h-full">
          {id === 'dynamic-duo' ? (
            <MultiFlavorSelector
              selectedFlavors={selectedFlavors}
              onFlavorsChange={(newFlavors) => {
                setSelectedFlavors(newFlavors);
              }}
              maxSelections={2}
              availableFlavors={availableFlavors}
            />
          ) : id === 'triple-threat' ? (
            <MultiFlavorSelector
              selectedFlavors={selectedFlavors}
              onFlavorsChange={(newFlavors) => {
                setSelectedFlavors(newFlavors);
              }}
              maxSelections={3}
              availableFlavors={availableFlavors}
            />
          ) : (
            <FlavorSelector
              selectedFlavor={flavor}
              onFlavorChange={(newFlavor) => {
                setFlavor(newFlavor);
              }}
              availableFlavors={availableFlavors}
            />
          )}

          <QuantitySelector quantity={quantity} onQuantityChange={setQuantity} productId={id} />

          <div className="flex-grow"></div>

          <button
            onClick={handleAddToCart}
            className="w-full bg-gradient-to-r from-[#ff00ff] via-[#ff66ff] to-[#00ffff] text-white px-6 py-4 rounded-xl font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-fuchsia-500/20 hover:shadow-fuchsia-500/30 mt-auto"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
            </svg>
            Add to Cart
          </button>

          {showToast && (
            <Toast
              message={`Added ${quantity} ${quantity === 1 ? 'pack' : 'packs'} of ${name} ${
                id === 'dynamic-duo' || id === 'triple-threat'
                  ? `with flavors: ${selectedFlavors.map((f, index) => `${index + 1}: ${f}`).join(', ')}`
                  : `(${flavor})`
              } to cart!`}
              type="success"
              onClose={() => {
                setShowToast(false);
              }}
              onViewCart={() => {
                setIsCartOpen(true);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
